/**
 * The entry page: what a person sees before the headset goes on.
 *
 * Carried over from Pale Signal's enter-VR overlay, and the reasons are worth
 * restating because they are not obvious:
 *
 *  - **DOM only, no engine import.** The whole file talks to a `SessionFacet`
 *    and nothing else, so it would serve a three.js or Babylon host unchanged.
 *  - **The session lifecycle belongs to the adapter, not here.** IWSDK's
 *    `launchXR()` returns `void` and attaches its own `.catch()`, so a
 *    dismissed permission prompt never reaches the caller and a button that
 *    called it directly sticks on "Starting…" until the page is reloaded. The
 *    facet resolves a result instead of throwing and owns the deadline.
 *  - **No failure mode is enumerated.** Denied permission, a dismissed prompt,
 *    an unsupported device, a headset already in a session: every one comes
 *    back as a result the button recovers from. The reason only picks wording.
 *  - **The app does not run until the headset is on.** The overlay follows
 *    visibility, so it covers the page in 2D and lifts in session.
 *
 * What is new here is the invitation. Pale Signal's gate is a button on a dark
 * gradient; this one has to say what the thing is before someone commits to
 * putting a headset on, so it carries a title, a line of what you can do, and a
 * real render of the atlas behind it (`scripts/capture-entry-image.mjs` makes
 * that picture from the client itself, so it can never promise a view the
 * client does not draw).
 */
import type { SessionFacet } from "@realitycollective/service-framework";

/**
 * How long to wait for a session before handing the button back. Generous: the
 * headset's permission prompt is a human-speed interaction, and a premature
 * reset is harmless whereas a button that never recovers is not.
 */
const LAUNCH_DEADLINE_MS = 15_000;

export interface EntryOverlayOptions {
  title?: string;
  standfirst?: string;
  action?: string;
  /** The button's label when it enters the screen build rather than a session. */
  screenAction?: string;
  /** Called when the gate lowers into the screen build instead of a session. */
  onDismiss?: () => void;
  /** Background image URL; the overlay still works without it. */
  image?: string;
  /** Attribution line, kept visible because the atlas is CC BY. */
  credit?: string;
}

export interface EntryOverlay {
  /** Progress line while the bundle loads; the button waits for `ready()`. */
  setProgress(text: string): void;
  /** Enable the button: the atlas is loaded and entering will be instant. */
  ready(): void;
  /** Report a load failure in the place the person is already looking. */
  fail(text: string): void;
}

export function installEntryOverlay(session: SessionFacet, options: EntryOverlayOptions = {}): EntryOverlay {
  const title = options.title ?? "Anatomy Atlas";
  const standfirst = options.standfirst ?? "A human body you can walk around, take apart and put back together.";
  const action = options.action ?? "Explore anatomy";

  const overlay = document.createElement("div");
  overlay.id = "entry-overlay";
  overlay.className = "entry";
  if (options.image) overlay.style.setProperty("--entry-image", `url("${options.image}")`);

  overlay.innerHTML = `
    <div class="entry-art" aria-hidden="true"></div>
    <div class="entry-scrim" aria-hidden="true"></div>
    <div class="entry-body">
      <p class="entry-eyebrow">Anatomy Atlas XR</p>
      <h1 class="entry-title"></h1>
      <p class="entry-standfirst"></p>
      <button class="entry-action" type="button"></button>
      <p class="entry-hint"></p>
      <p class="entry-credit"></p>
    </div>`;

  const el = <T extends HTMLElement>(selector: string): T => overlay.querySelector(selector) as T;
  el(".entry-title").textContent = title;
  el(".entry-standfirst").textContent = standfirst;
  el(".entry-credit").textContent = options.credit ?? "";
  const button = el<HTMLButtonElement>(".entry-action");
  const hint = el(".entry-hint");
  const screenAction = options.screenAction ?? "Explore on this screen";
  button.textContent = action;
  document.body.appendChild(overlay);

  let idleHint = "Put your headset on, then press to begin.";
  let loaded = false;
  /**
   * One button, and what it does depends on where the page is open. On a
   * headset's browser it enters the session. Anywhere else it lowers the gate
   * into the screen build, including on a PC with a headset attached, where
   * the screen is still what the person is looking at and the panel's own
   * Enter VR is a press away. Two buttons for the two cases were two paths
   * to one place, and the dead one got pressed.
   *
   * `xrAvailable` is what the probe said, `null` until it answers.
   */
  let xrAvailable: boolean | null = null;
  const headsetBrowser = /OculusBrowser|Quest|Pico|Wolvic|VRBrowser/i.test(navigator.userAgent);
  const noHeadset = "No headset here, so this explores on the screen. Open the page in a Quest browser for the immersive version.";
  const tetheredHeadset = "This explores on the screen. Enter VR from the panel puts a connected headset on.";
  /**
   * Set once the person has chosen the 2D build, so `sync` stops putting the
   * gate back. Without it a machine that cannot start a session is a machine
   * that can never get past this screen, and the desk build walks the same
   * scene the headset does.
   */
  let dismissed = false;

  /** Whether the button enters a session (a headset's browser with XR) or the screen build. */
  const entersSession = (): boolean => xrAvailable === true && headsetBrowser;
  const setIdle = (): void => {
    button.textContent = !loaded ? "Loading the atlas…" : entersSession() ? action : screenAction;
    button.disabled = !loaded;
    hint.textContent = !loaded ? "" : entersSession() ? idleHint : xrAvailable ? tetheredHeadset : noHeadset;
  };
  const setLaunching = (): void => {
    button.textContent = "Starting…";
    button.disabled = true;
  };
  /** Every failure lands here; the reason only chooses the wording. */
  const setFailed = (reason: string): void => {
    button.textContent = action;
    button.disabled = false;
    hint.textContent =
      reason === "timeout"
        ? "Could not start the session. If you dismissed the headset's prompt, press again to retry."
        : `XR unavailable (${reason}). Press again to retry.`;
  };

  /** The way in on this screen: lower the gate and let the desk build run. */
  const enterDesk = (): void => {
    dismissed = true;
    overlay.classList.add("is-hidden");
    options.onDismiss?.();
  };

  button.addEventListener("click", () => {
    if (!loaded) return;
    if (!entersSession()) {
      enterDesk();
      return;
    }
    if (session.getState() !== "none") return; // already requesting, or immersed
    setLaunching();
    void session.request("immersive-vr", { timeoutMs: LAUNCH_DEADLINE_MS }).then((result) => {
      if (result.ok) return; // `sync` hides the overlay when visibility follows
      console.warn("[Atlas] XR session not started:", result.reason);
      if (result.reason === "unsupported") {
        // the probe was wrong or absent; the person pressed to explore, so explore
        xrAvailable = false;
        enterDesk();
        return;
      }
      setFailed(result.reason);
    });
  });

  // "non-immersive" is the facet's value for "running in 2D with no session",
  // which is exactly when this gate should be up.
  const sync = (visibility: string): void => {
    const inSession = visibility !== "non-immersive";
    // Once the 2D build has been chosen the gate stays down, including after a
    // session ends. Entering XR is still a button away in the panel, and a gate
    // that reappears every time is a gate in the way.
    overlay.classList.toggle("is-hidden", inSession || dismissed);
    if (!inSession) setIdle();
  };

  setIdle();
  sync(session.getState() === "active" ? "visible" : "non-immersive");
  session.onVisibilityChange(sync);

  // Probe the device (a browser API, not an engine one) and let the idle
  // state follow: with no XR the button itself becomes the way in on this
  // screen, rather than a dead press beside a second button that works.
  const xr = (navigator as unknown as { xr?: XRSystem }).xr;
  const probed = (ok: boolean): void => {
    xrAvailable = ok;
    if (session.getState() === "none") setIdle();
  };
  if (!xr) {
    probed(false);
  } else {
    void xr
      .isSessionSupported("immersive-vr")
      .then(probed)
      .catch(() => probed(false));
  }

  return {
    setProgress: (text) => {
      if (!loaded) hint.textContent = text;
    },
    ready: () => {
      loaded = true;
      setIdle();
    },
    fail: (text) => {
      button.textContent = "Could not load";
      button.disabled = true;
      hint.textContent = text;
    },
  };
}
